import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';
import { ScanLine, Wrench, ShieldCheck, Crown, ArrowRight, Sparkles, Award } from 'lucide-react';

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [content, setContent] = useState(null);
    useEffect(() => {
        api.get('/products?limit=8').then(({ data }) => setFeatured(data.items || [])).catch(() => {});
        api.get('/site/content').then(({ data }) => setContent(data)).catch(() => {});
    }, []);

    const c = content || {};

    return (
        <div>
            {/* HERO */}
            <section className="relative rrm-bg-carbon">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 text-xs tracking-widest px-3 py-1 rounded-full bg-white/5 border border-white/10 text-amber-400 mb-6">
                            <Sparkles className="w-3.5 h-3.5" /> {c.hero_eyebrow || 'CURATED · NO GARBAGE PARTS'}
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95]">
                            {c.hero_title_line1 || 'REAL RUDE'} <span className="text-[var(--rrm-red)]">{c.hero_title_line2 || 'PERFORMANCE'}</span>
                        </h1>
                        <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl">
                            {c.hero_subtitle || 'OEM and VIN-verified upgrades for Dodge Durango / Challenger / Charger (R/T → Demon), Ford Mustang (2.3T & 5.0), and Audi sedans. Built by the club, for the club.'}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/shop"
                                data-testid="home-hero-shop-button"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm font-semibold"
                            >
                                Shop Parts <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/vin"
                                data-testid="home-hero-vin-button"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm"
                            >
                                <ScanLine className="w-4 h-4" /> Decode my VIN
                            </Link>
                            <Link
                                to="/join"
                                data-testid="home-hero-join-button"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 text-amber-200 text-sm"
                            >
                                <Crown className="w-4 h-4" /> Join RRM
                            </Link>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-6 text-xs text-white/50">
                            <div className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> VIN verified fitment</div>
                            <div className="inline-flex items-center gap-1.5"><Wrench className="w-4 h-4 text-amber-400" /> AI mechanic 24/7</div>
                            <div className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-rose-400" /> Apple Pay & Cash App</div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                            <img
                                src={c.hero_image_url || "https://images.pexels.com/photos/20706251/pexels-photo-20706251.jpeg?auto=compress&cs=tinysrgb&w=1200"}
                                alt={c.hero_image_alt || "Featured vehicle"}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4 rrm-panel p-3 flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] text-white/50 tracking-widest">{c.featured_eyebrow || 'FEATURED PLATFORM'}</div>
                                    <div className="font-display text-xl text-white">{c.featured_title || 'DODGE CHALLENGER'}</div>
                                </div>
                                <Link to="/shop" className="text-xs text-amber-300 inline-flex items-center gap-1">
                                    Browse <ArrowRight className="w-3 h-3" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* VEHICLE QUICK PICKS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="home-vehicle-quick-picks">
                <div className="flex items-end justify-between mb-6">
                    <h2 className="font-display text-3xl md:text-4xl text-white">SHOP YOUR PLATFORM</h2>
                    <Link to="/shop" className="text-sm text-amber-300 hover:underline">View all parts</Link>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { make: 'Dodge', label: 'Dodge HEMI', img: 'https://images.pexels.com/photos/13916690/pexels-photo-13916690.jpeg?auto=compress&cs=tinysrgb&w=900' },
                        { make: 'Ford',  label: 'Mustang 5.0 / 2.3T', img: 'https://images.pexels.com/photos/17340985/pexels-photo-17340985.jpeg?auto=compress&cs=tinysrgb&w=900' },
                        { make: 'Audi',  label: 'Audi A4 / S4 / A6', img: 'https://images.pexels.com/photos/9661335/pexels-photo-9661335.jpeg?auto=compress&cs=tinysrgb&w=900' },
                        { make: '',      label: 'More vehicles soon', img: 'https://images.pexels.com/photos/37687878/pexels-photo-37687878.jpeg?auto=compress&cs=tinysrgb&w=900' },
                    ].map((v) => (
                        <Link
                            key={v.label}
                            to={v.make ? `/shop?make=${v.make}` : '/shop'}
                            data-testid={`home-vehicle-tile-${v.make || 'more'}`}
                            className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-white/10"
                        >
                            <img src={v.img} alt={v.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                                <div className="font-display text-xl text-white">{v.label}</div>
                                <ArrowRight className="w-4 h-4 text-amber-300" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CURATED PROMISE */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="home-curated-promise">
                <div className="rrm-divider mb-10" />
                <div className="grid md:grid-cols-3 gap-4">
                    {[
                        { title: 'OEM Direct', body: 'Genuine Mopar, Motorcraft, and Audi factory parts. Exact replacements, every time.', icon: ShieldCheck },
                        { title: 'OEM-Equivalent Aftermarket', body: 'Only parts proven to match or exceed factory specs. Bolt-on confidence.', icon: Wrench },
                        { title: 'VIN-Specific Upgrades', body: 'Brembos, tunes, intakes — verified against your VIN before checkout.', icon: Sparkles },
                    ].map((c) => (
                        <div key={c.title} className="rrm-panel p-5">
                            <c.icon className="w-6 h-6 text-amber-400" />
                            <div className="mt-3 font-display text-xl text-white">{c.title}</div>
                            <div className="mt-1 text-sm text-white/60">{c.body}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-end justify-between mb-6">
                    <h2 className="font-display text-3xl md:text-4xl text-white">FEATURED PARTS</h2>
                    <Link to="/shop" className="text-sm text-amber-300 hover:underline">See all</Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {featured.slice(0, 8).map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            </section>

            {/* AI MECHANIC SECTION */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="home-ai-mechanic-section">
                <div className="rrm-panel p-6 md:p-10 grid lg:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="text-xs text-amber-400 tracking-widest mb-3">{c.ai_section_eyebrow || 'RRM MECHANIC AI'}</div>
                        <h3 className="font-display text-3xl md:text-4xl text-white">{c.ai_section_title || 'Stuck on a symptom? Ask the AI mechanic.'}</h3>
                        <p className="mt-3 text-white/60">{c.ai_section_body || 'Trained on the platforms we sell. Get step-by-step diagnostic guidance and OEM-correct part recs.'}</p>
                        <div className="mt-5 flex flex-wrap gap-2">
                            {['Misfire on HEMI', 'Audi 2.0T boost leak', 'Mustang GT clunk', 'Hellcat catch can', 'CEL on B9 S4'].map((cc) => (
                                <span key={cc} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{cc}</span>
                            ))}
                        </div>
                        <Link
                            to="/ai-mechanic"
                            className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm"
                            data-testid="home-ai-mechanic-cta"
                        >
                            Open AI Mechanic <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border border-white/10">
                        <img
                            src={c.ai_section_image_url || "https://images.pexels.com/photos/30470930/pexels-photo-30470930.jpeg?auto=compress&cs=tinysrgb&w=1200"}
                            alt="Mechanic workshop"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
